import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import Lottie from "lottie-react";
import api from "../utils/api";

import emptyStateAnimation from "../assets/food-cooking-animation.json";

/* ─── colour palette per offer type ─── */
const OFFER_THEME = {
  percentage: {
    gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    glow: "rgba(102,126,234,.25)",
    icon: "🏷️",
    label: "DISCOUNT",
  },
  fixed: {
    gradient: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
    glow: "rgba(17,153,142,.25)",
    icon: "💰",
    label: "FLAT OFF",
  },
  bogo: {
    gradient: "linear-gradient(135deg, #f857a6 0%, #ff5858 100%)",
    glow: "rgba(248,87,166,.25)",
    icon: "🎁",
    label: "BUY 1 GET 1",
  },
  free_item: {
    gradient: "linear-gradient(135deg, #fc4a1a 0%, #f7b733 100%)",
    glow: "rgba(252,74,26,.25)",
    icon: "🎉",
    label: "FREE ITEM",
  },
};

/* ─── Reusable CSS‑in‑JS (keeps component self‑contained) ─── */
const styles = {
  page: {
    minHeight: "100vh",
    background: "linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)",
    fontFamily: "'Inter', 'Segoe UI', system-ui, -apple-system, sans-serif",
    padding: "0 0 64px",
    overflow: "hidden",
    position: "relative",
  },
  /* floating decorative blobs */
  blob1: {
    position: "fixed",
    top: "-180px",
    right: "-120px",
    width: "420px",
    height: "420px",
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(248,87,166,.18) 0%, transparent 70%)",
    filter: "blur(60px)",
    pointerEvents: "none",
    zIndex: 0,
  },
  blob2: {
    position: "fixed",
    bottom: "-200px",
    left: "-140px",
    width: "500px",
    height: "500px",
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(102,126,234,.15) 0%, transparent 70%)",
    filter: "blur(80px)",
    pointerEvents: "none",
    zIndex: 0,
  },
  header: {
    textAlign: "center",
    padding: "48px 24px 12px",
    position: "relative",
    zIndex: 1,
  },
  headerEmoji: {
    fontSize: "48px",
    marginBottom: "12px",
    display: "block",
    animation: "bounce 2s ease-in-out infinite",
  },
  title: {
    fontSize: "clamp(1.8rem, 5vw, 3rem)",
    fontWeight: 800,
    letterSpacing: "-0.03em",
    color: "#fff",
    marginBottom: "8px",
    lineHeight: 1.15,
  },
  subtitle: {
    fontSize: "clamp(0.95rem, 2.5vw, 1.15rem)",
    color: "rgba(255,255,255,.55)",
    fontWeight: 400,
    maxWidth: "480px",
    margin: "0 auto",
    lineHeight: 1.5,
  },
  grid: {
    maxWidth: "720px",
    margin: "32px auto 0",
    padding: "0 16px",
    display: "flex",
    flexDirection: "column",
    gap: "20px",
    position: "relative",
    zIndex: 1,
  },
  /* ── card ── */
  card: (theme) => ({
    position: "relative",
    borderRadius: "20px",
    overflow: "hidden",
    background: "rgba(255,255,255,.06)",
    backdropFilter: "blur(24px) saturate(1.4)",
    WebkitBackdropFilter: "blur(24px) saturate(1.4)",
    border: "1px solid rgba(255,255,255,.1)",
    boxShadow: `0 8px 32px ${theme.glow}, 0 1px 0 rgba(255,255,255,.06) inset`,
    transition: "transform .28s cubic-bezier(.4,0,.2,1), box-shadow .28s ease",
    cursor: "default",
  }),
  cardHover: (theme) => ({
    transform: "translateY(-4px)",
    boxShadow: `0 20px 48px ${theme.glow}, 0 1px 0 rgba(255,255,255,.08) inset`,
  }),
  cardAccent: (theme) => ({
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "4px",
    background: theme.gradient,
  }),
  cardBody: {
    padding: "24px 20px",
    display: "flex",
    gap: "18px",
    alignItems: "flex-start",
  },
  /* badge circle */
  badge: (theme) => ({
    flexShrink: 0,
    width: "64px",
    height: "64px",
    borderRadius: "16px",
    background: theme.gradient,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "28px",
    boxShadow: `0 4px 20px ${theme.glow}`,
  }),
  cardContent: {
    flex: 1,
    minWidth: 0,
  },
  offerLabel: {
    display: "inline-block",
    fontSize: "11px",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "rgba(255,255,255,.45)",
    marginBottom: "4px",
  },
  offerTitle: {
    fontSize: "1.15rem",
    fontWeight: 700,
    color: "#fff",
    lineHeight: 1.3,
    marginBottom: "4px",
  },
  offerDesc: {
    fontSize: "0.88rem",
    color: "rgba(255,255,255,.5)",
    lineHeight: 1.5,
    marginBottom: "12px",
  },
  /* big value chip */
  valueChip: (theme) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    background: theme.gradient,
    padding: "6px 14px",
    borderRadius: "100px",
    fontSize: "0.85rem",
    fontWeight: 800,
    color: "#fff",
    letterSpacing: "-0.01em",
    boxShadow: `0 2px 12px ${theme.glow}`,
  }),
  metaRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
    marginTop: "12px",
  },
  metaPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: "5px",
    padding: "4px 10px",
    borderRadius: "100px",
    fontSize: "0.75rem",
    fontWeight: 600,
    background: "rgba(255,255,255,.07)",
    color: "rgba(255,255,255,.55)",
    border: "1px solid rgba(255,255,255,.08)",
  },
  /* countdown banner */
  countdown: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
    padding: "10px 20px",
    background: "rgba(255,255,255,.04)",
    borderTop: "1px solid rgba(255,255,255,.06)",
  },
  countdownLabel: {
    fontSize: "0.72rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    color: "rgba(255,255,255,.4)",
  },
  countdownBoxes: {
    display: "flex",
    gap: "6px",
  },
  countdownUnit: {
    textAlign: "center",
    minWidth: "40px",
  },
  countdownNum: {
    display: "block",
    fontSize: "1.1rem",
    fontWeight: 800,
    color: "#fff",
    lineHeight: 1,
    background: "rgba(255,255,255,.08)",
    borderRadius: "8px",
    padding: "6px 8px",
  },
  countdownSuffix: {
    display: "block",
    fontSize: "0.6rem",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: "rgba(255,255,255,.35)",
    marginTop: "3px",
  },
  /* loader */
  loader: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)",
  },
  spinner: {
    width: "40px",
    height: "40px",
    border: "3px solid rgba(255,255,255,.12)",
    borderTop: "3px solid #f857a6",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
  },
};

/* inject keyframes once */
const KEYFRAMES_ID = "public-offer-keyframes";
if (typeof document !== "undefined" && !document.getElementById(KEYFRAMES_ID)) {
  const sheet = document.createElement("style");
  sheet.id = KEYFRAMES_ID;
  sheet.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;800&display=swap');
    @keyframes spin { to { transform: rotate(360deg) } }
    @keyframes bounce {
      0%,100%{ transform:translateY(0) }
      50%{ transform:translateY(-8px) }
    }
    @keyframes fadeUp {
      from { opacity:0; transform:translateY(24px) }
      to   { opacity:1; transform:translateY(0) }
    }
  `;
  document.head.appendChild(sheet);
}

const PublicOfferPage = () => {
  const { restaurantId } = useParams();
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(new Date());
  const [hoveredId, setHoveredId] = useState(null);

  /* live clock for countdown */
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const getCountdownParts = (startDate) => {
    const diff = new Date(startDate) - now;
    if (diff <= 0) return null;
    return {
      d: Math.floor(diff / (1000 * 60 * 60 * 24)),
      h: Math.floor((diff / (1000 * 60 * 60)) % 24),
      m: Math.floor((diff / 1000 / 60) % 60),
      s: Math.floor((diff / 1000) % 60),
    };
  };

  useEffect(() => {
    const fetchPublicOffers = async () => {
      try {
        const response = await api.get(`/api/offers/public/${restaurantId}`);
        setOffers(response.data);
      } catch (error) {
        console.error("Failed to fetch offers", error);
      } finally {
        setLoading(false);
      }
    };
    if (restaurantId) fetchPublicOffers();
  }, [restaurantId]);

  /* ─── LOADING ─── */
  if (loading) {
    return (
      <div style={styles.loader}>
        <div style={styles.spinner} />
      </div>
    );
  }

  /* ─── EMPTY STATE ─── */
  if (offers.length === 0) {
    return (
      <div style={{ ...styles.page, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
        <div style={styles.blob1} />
        <div style={styles.blob2} />
        <div style={{ position: "relative", zIndex: 1, textAlign: "center", padding: "40px 24px", maxWidth: "420px" }}>
          <div style={{ width: "220px", height: "220px", margin: "0 auto 24px" }}>
            <Lottie animationData={emptyStateAnimation} loop className="w-full h-full" style={{ filter: "drop-shadow(0 8px 24px rgba(0,0,0,.4))" }} />
          </div>
          <h1 style={{ ...styles.title, fontSize: "clamp(1.6rem, 5vw, 2.4rem)" }}>Fresh Offers Are Cooking!</h1>
          <p style={{ ...styles.subtitle, marginTop: "12px" }}>
            We're whipping up something delicious. Check back soon for exclusive deals and discounts!
          </p>
        </div>
      </div>
    );
  }

  /* ─── helpers ─── */
  const getValueText = (offer) => {
    switch (offer.offerType) {
      case "percentage":
        return `${offer.discountValue}% OFF`;
      case "fixed":
        return `₹${offer.discountValue} OFF`;
      case "bogo": {
        const targets = offer.bogoTargetNames?.length ? offer.bogoTargetNames.join(", ") : "";
        if (offer.bogoAppliesTo === "all") return "Buy 1 Get 1 Free on Everything!";
        return `Buy 1 Get 1 on ${targets}`;
      }
      case "free_item":
        return `Free ${offer.freeItemName || "Item"}!`;
      default:
        return "";
    }
  };

  const getSubInfo = (offer) => {
    if (offer.offerType === "percentage" && offer.maxDiscountCap) {
      return `Save up to ₹${offer.maxDiscountCap}`;
    }
    return null;
  };

  /* ─── ACTIVE OFFERS ─── */
  return (
    <div style={styles.page}>
      <div style={styles.blob1} />
      <div style={styles.blob2} />

      {/* Header */}
      <header style={styles.header}>
        <span style={styles.headerEmoji}>🔥</span>
        <h1 style={styles.title}>Exclusive Offers</h1>
        <p style={styles.subtitle}>Grab these limited-time deals before they're gone</p>
      </header>

      {/* Cards */}
      <div style={styles.grid}>
        {offers.map((offer, i) => {
          const theme = OFFER_THEME[offer.offerType] || OFFER_THEME.percentage;
          const isUpcoming = new Date(offer.startDate) > now;
          const countdown = isUpcoming ? getCountdownParts(offer.startDate) : null;
          const isHovered = hoveredId === offer._id;

          return (
            <div
              key={offer._id}
              style={{
                ...styles.card(theme),
                ...(isHovered ? styles.cardHover(theme) : {}),
                animation: `fadeUp .5s ${i * 0.08}s both cubic-bezier(.4,0,.2,1)`,
                opacity: isUpcoming ? 0.85 : 1,
              }}
              onMouseEnter={() => setHoveredId(offer._id)}
              onMouseLeave={() => setHoveredId(null)}
            >
              {/* top accent bar */}
              <div style={styles.cardAccent(theme)} />

              <div style={styles.cardBody}>
                {/* Icon badge */}
                <div style={styles.badge(theme)}>{theme.icon}</div>

                {/* Content */}
                <div style={styles.cardContent}>
                  <span style={styles.offerLabel}>{theme.label}</span>
                  <h2 style={styles.offerTitle}>{offer.title}</h2>
                  {offer.description && <p style={styles.offerDesc}>{offer.description}</p>}

                  {/* Value chip */}
                  <div style={styles.valueChip(theme)}>{getValueText(offer)}</div>

                  {/* Sub‑info */}
                  {getSubInfo(offer) && (
                    <div style={{ fontSize: "0.78rem", color: "rgba(255,255,255,.4)", marginTop: "6px", fontWeight: 600 }}>
                      {getSubInfo(offer)}
                    </div>
                  )}

                  {/* Meta pills */}
                  <div style={styles.metaRow}>
                    {offer.minOrderValue > 0 && (
                      <span style={styles.metaPill}>💵 Min ₹{offer.minOrderValue}</span>
                    )}
                    <span style={styles.metaPill}>
                      📅 {new Date(offer.endDate).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                    </span>
                  </div>
                </div>
              </div>

              {/* Countdown footer (only for upcoming offers) */}
              {countdown && (
                <div style={styles.countdown}>
                  <span style={styles.countdownLabel}>⏳ Starts in</span>
                  <div style={styles.countdownBoxes}>
                    {countdown.d > 0 && (
                      <div style={styles.countdownUnit}>
                        <span style={styles.countdownNum}>{countdown.d}</span>
                        <span style={styles.countdownSuffix}>Day{countdown.d > 1 ? "s" : ""}</span>
                      </div>
                    )}
                    <div style={styles.countdownUnit}>
                      <span style={styles.countdownNum}>{String(countdown.h).padStart(2, "0")}</span>
                      <span style={styles.countdownSuffix}>Hrs</span>
                    </div>
                    <div style={styles.countdownUnit}>
                      <span style={styles.countdownNum}>{String(countdown.m).padStart(2, "0")}</span>
                      <span style={styles.countdownSuffix}>Min</span>
                    </div>
                    <div style={styles.countdownUnit}>
                      <span style={styles.countdownNum}>{String(countdown.s).padStart(2, "0")}</span>
                      <span style={styles.countdownSuffix}>Sec</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PublicOfferPage;
